import { useId, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

import Button from 'components/ui/button';
import { FOCO_VISIVEL } from 'utils/foco';

import { LIMITE_DA_COLAGEM, extrairChaves } from './colagem-de-chaves';
import { AvisoDeValidacao, CAMPO, ROTULO } from './componentes-do-cadastro';

export interface RecusaDaColagem {
  chave: string;
  motivo: string;
}

export interface ResumoDaColagem {
  incluidos: number;
  recusas: RecusaDaColagem[];
  /** Chaves cuja consulta falhou: voltam para o campo, para tentar de novo (RCP-18). */
  chavesComFalha: string[];
}

interface ColagemDeChavesProps {
  /** Consulta e inclui as chaves; informa o andamento por `onProgresso` e devolve o resumo. */
  onIncluir: (chaves: string[], onProgresso: (feitas: number, total: number) => void) => Promise<ResumoDaColagem>;
}

type Andamento = { feitas: number; total: number };

/**
 * Inclusão de vários participantes de uma vez, colando as chaves (RCP-10 a RCP-22). O componente só extrai as chaves e
 * mostra andamento, avisos e resumo; quem usa consulta, aplica as regras de inclusão e grava.
 */
export default function ColagemDeChaves({ onIncluir }: ColagemDeChavesProps) {
  const campoId = useId();
  const dicaId = useId();
  const [aberta, setAberta] = useState(false);
  const [texto, setTexto] = useState('');
  const [aviso, setAviso] = useState<string>();
  const [andamento, setAndamento] = useState<Andamento>();
  const [resumo, setResumo] = useState<ResumoDaColagem>();

  async function incluirChaves() {
    const chaves = extrairChaves(texto);
    setResumo(undefined);

    if (!chaves.length) {
      setAviso('Nenhuma matrícula no formato F0000000 foi encontrada.');
      return;
    }

    if (chaves.length > LIMITE_DA_COLAGEM) {
      setAviso(`Cole no máximo ${LIMITE_DA_COLAGEM} matrículas por vez.`);
      return;
    }

    setAviso(undefined);
    setAndamento({ feitas: 0, total: chaves.length });

    try {
      const novoResumo = await onIncluir(chaves, (feitas, total) => setAndamento({ feitas, total }));
      setResumo(novoResumo);
      setTexto(novoResumo.chavesComFalha.join('\n'));
    } finally {
      setAndamento(undefined);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        aria-expanded={aberta}
        onClick={() => setAberta(atual => !atual)}
        className={`flex items-center gap-1.5 self-start text-xs font-bold text-brand-blue hover:underline ${FOCO_VISIVEL}`}
      >
        Incluir várias matrículas
        {aberta ? <FiChevronUp aria-hidden="true" size={16} /> : <FiChevronDown aria-hidden="true" size={16} />}
      </button>

      {aberta && (
        <div className="flex flex-col gap-2.5 rounded-jj-lg bg-surface-100 px-4 py-3.5">
          <div>
            <label htmlFor={campoId} className={ROTULO}>
              Cole as matrículas dos participantes
            </label>
            <textarea
              id={campoId}
              rows={4}
              value={texto}
              aria-describedby={dicaId}
              onChange={evento => setTexto(evento.target.value)}
              placeholder="F1234567, F7654321…"
              className={`${CAMPO} ${FOCO_VISIVEL} resize-y`}
            />
            <p id={dicaId} className="m-0 mt-1 text-jj-label text-content">
              Separe as matrículas por vírgula, espaço ou quebra de linha. Até {LIMITE_DA_COLAGEM} por vez.
            </p>
          </div>

          <Button variant="secundario" size="sm" className="self-start" disabled={!!andamento} onClick={incluirChaves}>
            Incluir matrículas
          </Button>

          {aviso && <AvisoDeValidacao>{aviso}</AvisoDeValidacao>}

          {andamento && (
            <p role="status" className="m-0 animate-pulse text-xs font-bold text-brand-blue-dark">
              Verificando {andamento.feitas} de {andamento.total}…
            </p>
          )}

          {resumo && <ResumoDaInclusao resumo={resumo} />}
        </div>
      )}
    </div>
  );
}

function ResumoDaInclusao({ resumo }: { resumo: ResumoDaColagem }) {
  return (
    <div role="status" className="flex flex-col gap-1.5 text-xs text-content">
      <p className="m-0 font-bold text-brand-blue-dark">
        {resumo.incluidos} {resumo.incluidos === 1 ? 'incluído' : 'incluídos'}
      </p>
      {resumo.recusas.length > 0 && (
        <>
          <p className="m-0">Não incluídos:</p>
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {resumo.recusas.map(recusa => (
              <li key={recusa.chave}>
                <b>{recusa.chave}</b>: {recusa.motivo}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
